<template>
    <!-- CORREZIONE UI-02C: markup Bootstrap di riferimento fornito dall'utente.
         Struttura: blocco titolo+valore (.flex-fill) | icona (ph-2x),
         progress Bootstrap 0.125rem, footer con badge float-end. -->
    <div class="card card-body h-100 ui02-kpi" :aria-busy="loading ? 'true' : 'false'">

        <template v-if="loading">
            <div class="placeholder-glow">
                <span class="placeholder col-6 mb-2"></span>
                <span class="placeholder col-8 placeholder-lg mb-1"></span>
                <span class="placeholder col-4"></span>
            </div>
        </template>

        <template v-else-if="nonDisponibile">
            <div class="d-flex align-items-center mb-3">
                <div class="flex-fill fw-bold">
                    <h6 class="mb-0">{{ label }}</h6>
                    <span class="text-muted ui-kpi-value">&mdash;</span>
                </div>
            </div>
            <div class="ui-kpi-comparison">Dati non disponibili</div>
        </template>

        <template v-else>
            <div class="d-flex align-items-center mb-3">
                <div class="flex-fill fw-bold">
                    <h6 class="mb-0">{{ label }}</h6>
                    <span class="text-muted ui-kpi-value">{{ valoreFormattato }}</span>
                </div>

                <i v-if="icona" class="ph ph-2x opacity-75 ms-3" :class="[icona, classeIcona]"></i>
            </div>

            <div class="progress mb-2" style="height: 0.125rem;">
                <div class="progress-bar" :class="classeBarra" :style="{ width: fillDecorativo + '%' }"></div>
            </div>

            <div class="ui-kpi-comparison">
                <template v-if="confrontoDisponibile">
                    <span class="float-end">
                        <span class="badge bg-light mb-0" :class="classeDelta">
                            <i class="align-middle" :class="iconaDelta"></i>
                            {{ deltaPercentFormattato }}
                        </span>
                    </span>
                    vs. periodo precedente
                </template>
                <template v-else>
                    Confronto non disponibile
                </template>
            </div>
        </template>

    </div>
</template>

<script>
const FORMAT_EUR = new Intl.NumberFormat("it-IT", {
    style: "currency",
    currency: "EUR",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
});
const FORMAT_NUM = new Intl.NumberFormat("it-IT", { maximumFractionDigits: 0 });
const FORMAT_PERCENT = new Intl.NumberFormat("it-IT", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
});

export default {
    name: "KpiCard",

    props: {
        label: { type: String, required: true },
        indicatore: { type: Object, default: null },
        loading: { type: Boolean, default: false },
        unita: { type: String, default: "EUR" },
        icona: { type: String, default: "" },
        /* Tema presentazionale: blue/red/green/purple (mappa su classi tema). */
        tema: { type: String, default: "" },
    },
    computed: {
        nonDisponibile() {
            return !this.indicatore;
        },
        valore() {
            return this.nonDisponibile ? null : this.indicatore.valore;
        },
        unitaEffettiva() {
            return this.indicatore && this.indicatore.unita ? this.indicatore.unita : this.unita;
        },
        confrontoDisponibile() {
            return !this.nonDisponibile && this.indicatore.confronto_disponibile === true;
        },
        mostraConfronto() {
            return !this.nonDisponibile;
        },
        formato() {
            if (this.unitaEffettiva === "NUMERO") return FORMAT_NUM;
            if (this.unitaEffettiva === "PERCENT") return FORMAT_PERCENT;
            return FORMAT_EUR;
        },
        suffisso() {
            if (this.unitaEffettiva === "PERCENT") return " %";
            return "";
        },
        valoreFormattato() {
            const v = this.valore;
            if (v === null || v === undefined) return "-";
            return this.formato.format(v) + this.suffisso;
        },
        precedenteFormattato() {
            if (this.nonDisponibile) return "-";
            const p = this.indicatore.precedente;
            if (p === null || p === undefined) return "-";
            return this.formato.format(p) + this.suffisso;
        },
        delta() {
            return this.nonDisponibile ? null : this.indicatore.delta;
        },
        deltaFormattato() {
            if (this.nonDisponibile || this.delta === null || this.delta === undefined) return "-";
            const segno = this.delta > 0 ? "+" : "";
            return segno + this.formato.format(this.delta) + this.suffisso;
        },
        /* delta_percent null resta "-": NON diventa 0%. */
        deltaPercentFormattato() {
            if (this.nonDisponibile) return "-";
            const p = this.indicatore.delta_percent;
            if (p === null || p === undefined) return "-";
            const segno = p > 0 ? "+" : "";
            return segno + FORMAT_NUM.format(Math.round(p)) + "%";
        },
        deltaPositivo() {
            return this.delta !== null && this.delta !== undefined && this.delta > 0;
        },
        deltaNegativo() {
            return this.delta !== null && this.delta !== undefined && this.delta < 0;
        },
        /* Colore del badge: succes/danger/muted sul badge Bootstrap bg-light. */
        classeDelta() {
            if (this.deltaPositivo) return "text-success";
            if (this.deltaNegativo) return "text-danger";
            return "text-body-secondary";
        },
        iconaDelta() {
            if (this.deltaPositivo) return "ph-arrow-up-right";
            if (this.deltaNegativo) return "ph-arrow-down-right";
            return "ph-minus";
        },
        /* Valore negativo: colore di attenzione. */
        classeValore() {
            const v = this.valore;
            if (v === null || v === undefined) return "text-muted";
            return v < 0 ? "text-danger" : "";
        },
        /* Tema presentazionale della KPI (default: indigo come la reference). */
        temaEffettivo() {
            const t = (this.tema || "").toLowerCase();
            return ["blue", "red", "green", "purple"].indexOf(t) >= 0 ? t : "blue";
        },
        classeIcona() {
            const m = { blue: "text-indigo", red: "text-danger", green: "text-success", purple: "text-purple" };
            return m[this.temaEffettivo];
        },
        classeBarra() {
            const m = { blue: "bg-indigo", red: "bg-danger", green: "bg-success", purple: "bg-purple" };
            return m[this.temaEffettivo];
        },
        /* UI-02C: FILL DECORATIVA della progress bar.
           Nessun significato analitico (nessun tooltip/label che la lega a una
           metrica): valore visivo FISSO per tema (blue 60 / red 55 / green 50 /
           purple 60) per replicare il target anche senza periodo precedente.
           Mai invisibile. */
        fillDecorativo() {
            const m = { blue: 60, red: 55, green: 50, purple: 60, amber: 55, slate: 55 };
            return m[this.temaEffettivo] || 55;
        },
    },
}
</script>

<style scoped>
/* CORREZIONE UI-02C - KPI sul markup Bootstrap di riferimento.
   Nessun padding/margin/min-height custom, nessuna pill arrotondata custom:
   la geometria verticale e' del parent (--ui02-h) e le 4 KPI sono h-100. */
.ui02-kpi {
    border: 1px solid #e9edf2;
    border-radius: 14px;
    box-shadow: 0 1px 2px rgba(15, 23, 42, .04);
}
.ui02-kpi:hover {
    box-shadow: 0 6px 18px rgba(15, 23, 42, .08);
}
/* Gerarchia della reference: titolo (h6) + valore 1.5rem in text-muted. */
.ui-kpi-value {
    display: block;
    font-size: 1.5rem;
    line-height: 1.25;
}
.ui-kpi-comparison {
    color: #64748b;
    font-size: .8rem;
    line-height: 1.35;
}
</style>